import React, { useMemo, useCallback, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import type { Dispatch } from '@tmapy/redux';
import { useMessage, useSelectMessages } from '@tmapy/intl';
import { unaccentAndLower } from '@tmapy/beetlejuice-utils';

import type { MultiSelectProps } from '@tmapy/style-guide';
import {
  BtnBar,
  Form,
  FormItem,
  FormRow,
  Input,
  CtaBtn,
  MultiSelect,
  SecondaryBtn,
  StickyFooter,
} from '@tmapy/style-guide';

import type { DmxConfig, OmittedFormItemProps } from '../../types';
import type { InputProps } from '../../hooks/useInput';
import { useInput, useMultiSelect } from '../../hooks';

import type { DocsListQuery, DocsListResponse } from '../../openapi/operations/docsList';
import { FlowEnum } from '../../openapi/types/FlowEnum';
import { setDocListFilter } from '../../redux/filter/actions';

import { DOC_STATE, DOC_STATES_FILTER_KAD } from '../constants';

import {
  docFormMessages,
  docStateMessages,
  docFilterMessages,
  docInternalInfoMessages,
} from './docsMessages';
import { useCanManageDocInternalInfo } from './DocInternalInfo';

const messagesParams = {
  id: FlowEnum.DTM_KAD,
};

type DocFiltersProps = {
  filters: DocsListQuery;
  customerType: DmxConfig['customerType'];
  dispatch: Dispatch;
};

export const DocFilterDtmKad: React.FC<DocFiltersProps> = ({ filters, dispatch }) => {
  const formatMessage = useMessage();

  // ID
  const id = useInput<InputProps, number>({
    name: 'id',
    initialValue: filters.id,
    label: formatMessage(docFormMessages.id),
  });

  // NAME
  const name = useInput<InputProps>({
    name: 'name__icontains',
    initialValue: filters.name__icontains,
    label: formatMessage(docFormMessages.name),
  });

  // DESCRIPTION
  const description = useInput<InputProps>({
    name: 'description__icontains',
    initialValue: filters.description__icontains,
    label: formatMessage(docFormMessages.description),
  });

  // GEODESIST_FULL_NAME
  const geodesistFullName = useInput<InputProps>({
    name: 'geodesist_full_name',
    initialValue: filters.geodesist_full_name,
    label: formatMessage(docFormMessages.geodesist),
  });

  // STATE
  const stateItems = useSelectMessages(
    docStateMessages,
    DOC_STATES_FILTER_KAD as any,
    messagesParams,
  );

  const prepareStateItems = useCallback(
    (val: DOC_STATE) => {
      return stateItems.filter(
        (state) => unaccentAndLower(state.label).indexOf(unaccentAndLower(val)) >= 0,
      );
    },
    [stateItems],
  );
  const stateInitialValues = useMemo(() => {
    return filters.state__in?.length
      ? stateItems.filter((state) => filters.state__in!.includes(state.value))
      : [];
  }, [stateItems, filters.state__in]);

  const state__in = useMultiSelect<
    MultiSelectProps<DocsListResponse> | OmittedFormItemProps,
    (typeof stateItems)[0]
  >({
    name: 'state__in',
    label: formatMessage(docFormMessages.state),
    initialValue: stateInitialValues,
    prepareItems: prepareStateItems,
    renderValue: (state: (typeof stateItems)[0]) => state.label,
    renderItem: (state: (typeof stateItems)[0]) => state.label,
  });

  // CREATED (from)
  const [createdGte, setCreateGt] = useState(filters.created__gte);
  const created__gte = {
    name: 'created__gte',
    clear: () => {
      setCreateGt('');
    },
  };

  const handleCreateGtChange = (
    e: React.ChangeEvent,
    eventData: { name: string; value: string },
  ) => {
    setCreateGt(eventData.value);
  };

  // CREATED (to)
  const [createdLt, setCreateLt] = useState(filters.created__lt);
  const created__lt = {
    name: 'created__lt',
    clear: () => {
      setCreateLt('');
    },
  };

  const handleCreateLtChange = (
    e: React.ChangeEvent,
    eventData: { name: string; value: string },
  ) => {
    setCreateLt(eventData.value);
  };

  // FINISHED__GTE
  const [finishedGte, setFinishedGte] = useState(filters.finished__gte);
  const finished__gte = {
    name: 'finished__gte',
    clear: () => {
      setFinishedGte('');
    },
  };
  const handleFinishedGteChange = (
    e: React.ChangeEvent,
    eventData: { name: string; value: string },
  ) => {
    setFinishedGte(eventData.value);
  };
  // FINISHED__LTE
  const [finishedLte, setFinishedLte] = useState(filters.finished__lte);
  const finished__lte = {
    name: 'finished__lte',
    clear: () => {
      setFinishedLte('');
    },
  };
  const handleFinishedLteChange = (
    e: React.ChangeEvent,
    eventData: { name: string; value: string },
  ) => {
    setFinishedLte(eventData.value);
  };

  const responsiblePersonFullName = useInput<InputProps>({
    name: 'responsible_person_full_name',
    initialValue: filters.responsible_person_full_name,
    label: formatMessage(docInternalInfoMessages.responsiblePerson),
  });
  const canManageInternalInfo = useCanManageDocInternalInfo();

  const filterClearScope = [
    name,
    description,
    state__in,
    id,
    geodesistFullName,
    created__gte,
    created__lt,
    finished__gte,
    finished__lte,
    responsiblePersonFullName,
  ];

  const handleClearBtnClick = useCallback(() => {
    filterClearScope.forEach((formItem) => formItem?.clear?.());
    dispatch(
      setDocListFilter({
        prejimka_type__in: ['DTM_KAD'],
      }),
    );
  }, [dispatch]);

  function handleSubmit() {
    dispatch(
      setDocListFilter({
        name__icontains: name.value,
        description__icontains: description.value,
        state__in: state__in.values.map((state) => state.value),
        id: id.value,
        prejimka_type__in: ['DTM_KAD'],
        geodesist_full_name: geodesistFullName.value,
        responsible_person_full_name: responsiblePersonFullName.value,
        created__gte: createdGte,
        created__lt: createdLt,
        finished__gte: finishedGte,
        finished__lte: finishedLte,
      }),
    );
  }

  return (
    <Form onSubmit={handleSubmit}>
      <div className='sg-u-vs-2'>
        <FormRow>
          <FormItem {...name}>
            <Input {...name} />
          </FormItem>
          <FormItem {...description}>
            <Input {...description} />
          </FormItem>
        </FormRow>

        <FormRow>
          <FormItem {...state__in}>
            <MultiSelect {...state__in} />
          </FormItem>
          <FormItem {...id}>
            <Input {...id} />
          </FormItem>
        </FormRow>

        <FormRow>
          <FormItem {...geodesistFullName}>
            <Input {...geodesistFullName} />
          </FormItem>
        </FormRow>

        <FormRow>
          <FormItem label={formatMessage(docFormMessages.createdGte)}>
            <Input
              type='date'
              name='created__gte'
              value={createdGte}
              onChange={handleCreateGtChange}
            />
          </FormItem>
          <FormItem label={formatMessage(docFormMessages.createdLt)}>
            <Input
              type='date'
              name='created__lt'
              value={createdLt}
              onChange={handleCreateLtChange}
            />
          </FormItem>
        </FormRow>

        <FormRow>
          <FormItem label={formatMessage(docFormMessages.finishedGte)}>
            <Input
              type='date'
              name='finished__gte'
              value={finishedGte}
              onChange={handleFinishedGteChange}
            />
          </FormItem>
          <FormItem label={formatMessage(docFormMessages.finishedLte)}>
            <Input
              type='date'
              name='finished__lte'
              value={finishedLte}
              onChange={handleFinishedLteChange}
            />
          </FormItem>
        </FormRow>

        {canManageInternalInfo && (
          <FormRow>
            <FormItem {...responsiblePersonFullName}>
              <Input {...responsiblePersonFullName} />
            </FormItem>
          </FormRow>
        )}
      </div>
      <StickyFooter>
        <BtnBar stretch>
          <CtaBtn type='submit'>
            <FormattedMessage {...docFilterMessages.submitBtn} />
          </CtaBtn>
          <SecondaryBtn isDanger onClick={handleClearBtnClick}>
            <FormattedMessage {...docFilterMessages.clearBtn} />
          </SecondaryBtn>
        </BtnBar>
      </StickyFooter>
    </Form>
  );
};
